import { useState } from "react"
import { Divider } from "@components"
import { getAvailableSessionExtensionTimes, mySqlToLocal } from "@helpers"

import { 
  Button, 
  Card, 
  CardBody, 
  Select, 
  SelectItem 
} from "@heroui/react"

import { ArrowRightIcon, CalendarDaysIcon } from "@heroicons/react/24/outline"
import PropTypes from "prop-types"

const ExtendSession = ({ 
  handleCancel, 
  handleSave, 
  isSaving, 
  session 
}) => {
  const [newStartTime, setNewStartTime] = useState(null)
  const [newEndTime, setNewEndTime] = useState(null)

  const totalSlotTime = (session.slot_duration + session.slot_gap)
  const startTime = mySqlToLocal(session.starts_at)
  const endTime = mySqlToLocal(session.ends_at)

  const getAppendedSlotsCount = () => {
    if (!newEndTime?.currentKey) return 0
    const newEndDateTime = endTime.clone().hour(newEndTime.currentKey.split(":")[0]).minute(newEndTime.currentKey.split(":")[1]).second(0)
    return newEndDateTime.diff(endTime, "minute") / totalSlotTime
  }

  const getPrependedSlotsCount = () => {
    if (!newStartTime?.currentKey) return 0
    const newStartDateTime = startTime.clone().hour(newStartTime.currentKey.split(":")[0]).minute(newStartTime.currentKey.split(":")[1]).second(0)
    return startTime.diff(newStartDateTime, "minute") / totalSlotTime
  }

  const isDirty = getAppendedSlotsCount() > 0 || getPrependedSlotsCount() > 0

  const startOptions = getAvailableSessionExtensionTimes(startTime, session.slot_duration, session.slot_gap, "before", 100).map(time => time.format("HH:mm"))
  const endOptions = getAvailableSessionExtensionTimes(endTime, session.slot_duration, session.slot_gap, "after", 100).map(time => time.format("HH:mm"))
  const totalSlotsAdded = getAppendedSlotsCount() + getPrependedSlotsCount()

  return (
    <div>
      <Card className="mb-6 bg-content2">
        <CardBody className="p-4 px-6">
          <p className="mb-2 text-sm">Current Session (Read Only)</p>
          
          <div className="flex items-center">
            <div className="flex items-center justify-center mr-4 rounded-full bg-content3">
              <CalendarDaysIcon className="p-2 w-14 h-14" />
            </div>
              
            <p className="text-2xl">{startTime.format("HH:mm")}</p>
            <ArrowRightIcon className="w-6 h-6 mx-4" />
            <p className="text-2xl">{endTime.format("HH:mm")}</p>
          </div>
        </CardBody>
      </Card>

      <div>
        <p className="mb-2 text-lg font-semibold">Extend Earlier (optional)</p>
        <p className="mb-2 text-sm">Choose a new start time</p>
        
        <Select onSelectionChange={(option) => setNewStartTime(option)} selectedKeys={newStartTime} aria-label="Start Time">
          {startOptions.map((option) => (
            <SelectItem key={option}>{option}</SelectItem>
          ))}
        </Select>
      </div>

      <Divider className="my-6" />
      
      <div>
        <p className="mb-2 text-lg font-semibold">Extend Later (optional)</p>
        <p className="mb-2 text-sm">Choose a new end time</p>
        
        <Select onSelectionChange={(option) => setNewEndTime(option)} selectedKeys={newEndTime} aria-label="End Time">
          {endOptions.map((option) => (
            <SelectItem key={option}>{option}</SelectItem>
          ))}
        </Select>
      </div>

      <Divider className="my-6" />

      <div>
        <p className="mb-2 text-lg font-semibold">Preview</p>

        <Card className="bg-content2">
          <CardBody className="p-4 px-6">
            <div className="flex justify-between">
              <p className="mb-2 text-sm">New session time</p>
              <p className="mb-2 text-sm">Slots added</p>
            </div>

            <div className="flex items-center">
              <div className="flex items-center">
                <div className="flex items-center justify-center mr-4 bg-indigo-600 rounded-full">
                  <CalendarDaysIcon className="p-2 text-white w-14 h-14" />
                </div>
                  
                <p className="text-2xl">{newStartTime?.currentKey || startTime.format("HH:mm")}</p>
                <ArrowRightIcon className="w-6 h-6 mx-4" />
                <p className="text-2xl">{newEndTime?.currentKey || endTime.format("HH:mm")}</p>
              </div>
                  
              <p className="w-full text-2xl text-right">{totalSlotsAdded}</p>
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="flex flex-col gap-2 mt-6">
        <Button color="default" fullWidth onPress={handleCancel}>Cancel</Button>
        
        <Button 
          className="font-semibold" 
          type="submit" 
          color="secondary" 
          fullWidth 
          isDisabled={!isDirty} 
          isLoading={isSaving}
          onPress={() => handleSave(newStartTime?.currentKey || startTime.format("HH:mm"), newEndTime?.currentKey || endTime.format("HH:mm"), totalSlotsAdded)}
        >
          Extend Session
        </Button>
      </div>
    </div>
  )
}

ExtendSession.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  session: PropTypes.object.isRequired,
}

export default ExtendSession